import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {Polygon} from "@luciad/ria/shape/Polygon.js";
import * as React from "react";
import {Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, Modal} from "react-bootstrap/lib";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {RESTRICT_NAVIGATION_BOUNDS} from "../common/ui/riamap/Previewer";
import {RIAMap} from "../common/ui/riamap/RIAMap";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AOINotificationArea, AOIMonitor} from "./model";

const LABEL_COL = 2;
const CONTROL_COL = 10;

export interface AOIDialogOwnProps {
  onSuccess?: () => void;
  onHide?: () => void;
  notification: AOIMonitor;
  notificationAreaIndex: number;
}

type AOIDialogProps = AOIDialogOwnProps & InjectedIntlProps & WithApiProperties;

interface AOIDialogState {
  isExecuting: boolean;
  isSuccess: boolean;
  error: Error;
  name: string;
  mapDrawModeActive: boolean;
  mapEditModeActive: boolean;
  featureCoordinates: number[];
  featureCoordinatesBefore: number[];
  notificationArea?: AOINotificationArea;
}

class AOIDialogComponent extends React.Component<AOIDialogProps, AOIDialogState> {

  constructor(props: AOIDialogProps) {
    super(props);

    const notificationAreas = props.notification.triggers;
    const notificationArea = props.notificationAreaIndex == null ? null
                                                                 : notificationAreas[props.notificationAreaIndex];

    //todo: might need to change this later to handle POLYGON, etc
    const aoiGeometry = [];
    if (notificationArea && notificationArea.geomWkt) {
      const coordinatesStrings = notificationArea.geomWkt.substring(notificationArea.geomWkt.lastIndexOf("(") + 1,
          notificationArea.geomWkt.indexOf(")")).split(',');

      coordinatesStrings.forEach((coordinates) => {
        const coordinate = coordinates.trim().split(' ');
        if (coordinate.length === 2) {
          if (!isNaN(parseFloat(coordinate[0])) && !isNaN(parseFloat(coordinate[1]))) {
            aoiGeometry.push(parseFloat(coordinate[0]));
            aoiGeometry.push(parseFloat(coordinate[1]));
          }
        }
      });
    }

    this.state = {
      isExecuting: false,
      error: null,
      isSuccess: false,
      notificationArea,
      name: notificationArea?.name || "",
      mapDrawModeActive: false,
      mapEditModeActive: false,
      featureCoordinates: aoiGeometry,
      featureCoordinatesBefore: [],
    };
  }

  render() {
    return (
        <div>
          {this.renderModal()}
        </div>
    );
  }

  private closeDialog = () => {
    this.props.onHide();
  }

  private renderTitle() {
    const {notificationAreaIndex} = this.props;
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }

    if (notificationAreaIndex !== null) {
      return <Modal.Title><FormattedMessage id="studio.notifications.aoi-dialog.edit-aoi"
                                            defaultMessage="Edit AOI"/></Modal.Title>;
    }

    return <Modal.Title><FormattedMessage id="studio.notifications.create-aoi"
                                          defaultMessage="Create AOI"/></Modal.Title>;
  }

  private renderButtons() {
    const {notificationAreaIndex} = this.props;
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }

    return (
        <div>
          <Button onClick={this.submit}
                  disabled={this.state.name.length === 0 || this.state.featureCoordinates.length === 0}>
            {notificationAreaIndex == null &&
             <FormattedMessage id="studio.form.create" defaultMessage="Create"/>}
            {notificationAreaIndex != null && <FormattedMessage id="studio.form.save" defaultMessage="Save"/>}
          </Button>

          <Button onClick={this.closeDialog}><FormattedMessage id="studio.form.cancel"
                                                               defaultMessage="Cancel"/></Button>
        </div>
    );
  }

  private renderExecuting() {
    const {isExecuting} = this.state;
    if (isExecuting) {
      return (
          <div style={{textAlign: "center"}}>
            <h2><FormattedMessage id="studio.notifications.aoi-dialog.saving" defaultMessage="Saving AOI..."/>
            </h2>
            <Spinner bigStyle/>
          </div>
      );
    }
    return null;
  }

  private renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  private renderBody() {
    const {isExecuting} = this.state;

    if (isExecuting) {
      return this.renderExecuting();
    }

    const {api} = this.props;
    const background = api.getPreviewBackground();

    return (
        <div>
          {this.renderError()}
          <Form horizontal>
            <FormGroup controlId="name">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.notifications.aoi-dialog.name"
                                  defaultMessage="Name"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="name" value={this.state.name} onChange={this.onChange}/>
              </Col>
            </FormGroup>
            <FormGroup controlId="riamap">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
              </Col>
              <Col sm={CONTROL_COL}>
                <RIAMap style={{width: "100%", height: "400px"}}
                        background={background}
                        dataToVisualize={[]}
                        shouldFit={true}
                        shouldAnimateFit={true}
                        restrictNavigationBounds={background.bounds ? background.bounds : RESTRICT_NAVIGATION_BOUNDS}
                        shouldHideLayerTree={true}
                        shouldAddGridLayer={false}
                        showPreview={true}
                        drawModeActive={this.state.mapDrawModeActive}
                        editModeActive={this.state.mapEditModeActive}
                        onDrawCancel={this.onDrawEditCancel}
                        onEditCancel={this.onDrawEditCancel}
                        editFeatureCoordinates={this.state.featureCoordinates}
                        alwaysShowEditFeature={true}
                        onFeatureDrawn={this.onFeatureDrawn}
                        button={this.riaMapButton()}
                />
              </Col>
            </FormGroup>
          </Form>
        </div>
    );
  }

  private onChange = (event: any) => {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;
    this.setState({[name]: value} as any);
    this.render()
  };

  private riaMapButton = () => {
    const {featureCoordinates} = this.state;

    return <div className="riamapButtons">
      <Button onClick={this.onDraw}>
        <FormattedMessage
            id="studio.ria-map.draw"
            defaultMessage="Draw"/>
      </Button>

      <Button onClick={this.onEdit} disabled={featureCoordinates.length === 0}>
        <FormattedMessage
            id="studio.ria-map.edit"
            defaultMessage="Edit"/>
      </Button>
    </div>;
  };

  private onDraw = () => {
    const {featureCoordinates} = this.state;
    this.setState({
      ...this.state,
      mapDrawModeActive: true,
      featureCoordinatesBefore: featureCoordinates,
      featureCoordinates: [],
    });
  };

  private onEdit = () => {
    const {featureCoordinates} = this.state;
    this.setState({
      ...this.state,
      mapEditModeActive: true,
      featureCoordinatesBefore: featureCoordinates,
    });
  };

  private onDrawEditCancel = () => {
    const {featureCoordinatesBefore} = this.state;

    this.setState({
      ...this.state,
      mapDrawModeActive: false,
      mapEditModeActive: false,
      featureCoordinates: featureCoordinatesBefore,
    });
  };

  private onFeatureDrawn = (feature: Feature): boolean => {
    const polygon = feature.shape as Polygon;
    const numPoints = polygon.pointCount;
    const coords: number[] = [];
    for (let i = 0; i < numPoints; i++) {
      const point = polygon.getPoint(i);
      coords.push(point.x);
      coords.push(point.y);
    }

    this.setState({
      ...this.state,
      featureCoordinates: coords,
      mapDrawModeActive: false,
      mapEditModeActive: false,
    });
    return true;
  };

  private renderModal() {
    const {isExecuting} = this.state;
    return (
        <Modal show={true} onHide={this.closeDialog} backdrop="static">
          <Modal.Header closeButton={!isExecuting}>
            {this.renderTitle()}
          </Modal.Header>
          <Modal.Body>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.renderButtons()}
          </Modal.Footer>
        </Modal>
    );
  }

  private submit = () => {
    const {notificationArea} = this.state;

    Promise.resolve()
        .then(() => {

          const {api, notification} = this.props;
          const {name, featureCoordinates} = this.state;

          this.setState({...this.state, isExecuting: true});

          let polygon = "POLYGON ((";
          for (let i = 0; i < featureCoordinates.length; i += 2) {
            polygon += featureCoordinates[i] + ' ' + featureCoordinates[i + 1] + ", ";
          }
          polygon += featureCoordinates[0] + ' ' + featureCoordinates[1] + "))";

          return api.updateAOINotification(notification, {
            id: notificationArea?.id,
            name,
            srs: "4326",
            geomWkt: polygon
          })
              .catch((error) => {
                this.setState({...this.state, isExecuting: false, error});

                throw error;
              })
              .then(() => {
                this.props.onSuccess();
              });
        });
  }
}

export const AOIDialog = WithApi(injectIntl(AOIDialogComponent));